import { z } from "zod";
/*
 * The payloads the rules read, defined once. Producers write through these
 * shapes and consumers read through them, so a field cannot be spelled one way
 * at the ingest and another in the rule that needs it.
 *
 * Each reader takes an unknown payload and returns null rather than throwing:
 * a signal that does not carry the field is the normal case, not an error.
 */
function read(schema, payload) {
    const parsed = schema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}
/** When the member fell asleep. Terra carries it on the sleep period's
 *  metadata; every onset, drift and wind-down rule needs it. */
const sleepOnsetSchema = z.object({ onset: z.string().min(1) });
export function sleepOnset(payload) {
    const hit = read(sleepOnsetSchema, payload);
    if (!hit)
        return null;
    const at = new Date(hit.onset);
    return Number.isNaN(at.getTime()) ? null : at;
}
/** The device's own activity class per slot on a fixed grid. Oura sends this
 *  and no step samples, so it is the only sub-day movement a ring reports. */
const activityLevelsSchema = z.object({
    levels: z.object({
        start: z.string().min(1),
        minutes: z.number().finite().positive(),
        classes: z.string().min(1),
    }),
});
export function activityLevels(payload) {
    return read(activityLevelsSchema, payload)?.levels ?? null;
}
/** Steps in a quarter-hour, from the wearable's own samples. Turns "how long
 *  have they been sitting" from an inference into a measurement. */
const stepBucketSchema = z.object({ at: z.string().min(1), steps: z.number().finite() });
const stepBucketsSchema = z.object({ buckets: z.array(stepBucketSchema) });
export function stepBuckets(payload) {
    const hit = read(stepBucketsSchema, payload);
    if (!hit)
        return [];
    return hit.buckets.flatMap((b) => {
        const at = new Date(b.at);
        return Number.isNaN(at.getTime()) ? [] : [{ at, steps: b.steps }];
    });
}
/** Precipitation in quarter-hour slices ahead. Outside North America and
 *  central Europe the provider interpolates these from the hourly model, so a
 *  slice is a forecast rather than a reading. */
const precipSliceSchema = z.object({ at: z.string().min(1), mm: z.number().finite() });
const precipSlicesSchema = z.object({ precipitationNext: z.array(precipSliceSchema) });
export function precipitationSlices(payload) {
    const hit = read(precipSlicesSchema, payload);
    if (!hit)
        return [];
    return hit.precipitationNext.flatMap((s) => {
        const at = new Date(s.at);
        return Number.isNaN(at.getTime()) ? [] : [{ at, mm: s.mm }];
    });
}
/** The first dry stretch of at least `minutes`, or null when the window ahead
 *  never clears. What "rain stops at 15:40, clear until 16:20" is built from. */
export function nextDryWindow(slices, minutes) {
    const SLICE_MINUTES = 15;
    const need = Math.ceil(minutes / SLICE_MINUTES);
    let run = [];
    for (const slice of slices) {
        run = slice.mm > 0 ? [] : [...run, slice];
        if (run.length >= need) {
            const from = run[0];
            const last = run[run.length - 1];
            if (!from || !last)
                return null;
            return { from: from.at, to: new Date(last.at.getTime() + SLICE_MINUTES * 60_000) };
        }
    }
    return null;
}
/** A span at the desk, from whichever producer saw it. */
const deskSessionSchema = z.object({
    startedAt: z.string().min(1),
    endedAt: z.string().min(1).nullable().optional(),
});
export function deskSession(payload) {
    const hit = read(deskSessionSchema, payload);
    if (!hit)
        return null;
    const startedAt = new Date(hit.startedAt);
    if (Number.isNaN(startedAt.getTime()))
        return null;
    const ended = hit.endedAt ? new Date(hit.endedAt) : null;
    return { startedAt, endedAt: ended && !Number.isNaN(ended.getTime()) ? ended : null };
}
/** Unbroken minutes at the desk up to `now`, across back-to-back sessions. */
export function deskMinutes(sessions, now) {
    const open = sessions.filter((s) => s.endedAt === null || s.endedAt > now);
    const start = open.reduce((earliest, s) => (earliest === null || s.startedAt < earliest ? s.startedAt : earliest), null);
    if (!start)
        return null;
    return Math.max(0, Math.round((now.getTime() - start.getTime()) / 60_000));
}
//# sourceMappingURL=signal-reads.js.map